import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { useCallback, useEffect, useState } from "react";

import { USE_AUTH } from "../../api/apiConfig";
import useQueryClient from "../../api/query";
import LoadingSkeleton from "../MaintenancePage/LoadingSkeleton";
import { COLORS, FONT_FAMILY } from "../MaintenancePage/styles";
import WieldyLogo from "../MaintenancePage/WieldyLogo";

type PageState =
  | { status: "loading" }
  | { status: "ready"; organizations: SwitchableOrganization[] };

function SelectOrganizationPage() {
  const { loginWithRedirect } = useAuth0();
  const queryClient = useQueryClient();
  const [state, setState] = useState<PageState>({ status: "loading" });

  // queryClient is a new instance every render (see WL-3035); empty deps keeps
  // this callback stable so the fetch effect below doesn't re-fire on every render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: adding queryClient.getSwitchableOrganizations would change when this runs
  const loadOrganizations = useCallback(
    () => queryClient.getSwitchableOrganizations(),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    loadOrganizations()
      .then((organizations) => {
        if (!cancelled) setState({ status: "ready", organizations });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "ready", organizations: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [loadOrganizations]);

  if (state.status === "loading") {
    return <LoadingSkeleton />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
        background: COLORS.bg,
      }}
    >
      <WieldyLogo />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 24,
          width: 560,
          maxWidth: "calc(100% - 32px)",
          background: COLORS.cardBg,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 16,
          padding: "48px 48px 40px 48px",
          boxShadow: "0 8px 32px rgba(10, 11, 15, 0.08)",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontFamily: FONT_FAMILY,
            fontSize: 28,
            fontWeight: 600,
            color: COLORS.textPrimary,
            letterSpacing: -0.5,
            textAlign: "center",
          }}
        >
          Choose an organization
        </h1>
        <p
          style={{
            margin: 0,
            fontFamily: FONT_FAMILY,
            fontSize: 15,
            fontWeight: 400,
            color: COLORS.textSecondary,
            lineHeight: 1.6,
            textAlign: "center",
          }}
        >
          The organization you were viewing is in maintenance. Pick another
          organization to continue.
        </p>
        <div style={{ width: "100%", height: 1, background: COLORS.border }} />
        {state.organizations.length === 0 ? (
          <p
            style={{
              margin: 0,
              fontFamily: FONT_FAMILY,
              fontSize: 14,
              color: COLORS.textSecondary,
            }}
          >
            No other organizations are available right now.
          </p>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              width: "100%",
            }}
          >
            {state.organizations.map((org) => (
              <button
                key={org.auth0OrgId}
                type="button"
                onClick={() => {
                  loginWithRedirect({
                    authorizationParams: {
                      organization: org.auth0OrgId,
                      org_name: org.name,
                    },
                  });
                }}
                style={{
                  fontFamily: FONT_FAMILY,
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#FFFFFF",
                  background: COLORS.dark,
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 24px",
                  cursor: "pointer",
                }}
              >
                {org.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const AuthenticatedSelectOrganizationPage = USE_AUTH
  ? withAuthenticationRequired(SelectOrganizationPage, {
      onRedirecting: () => <LoadingSkeleton />,
    })
  : SelectOrganizationPage;

export default AuthenticatedSelectOrganizationPage;
